import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T, photoAngles } from "../../constants";
import { useUi } from "../../ui";
import PhotoTile from "./PhotoTile";
import CameraCaptureModal from "./CameraCaptureModal";

const useStyles = makeStyles({
  box: { marginTop: 12, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, padding: 12 },
  /* Unframed, the set is the screen's own subject rather than a panel on it -
     the same shape intake gives it. */
  plain: { marginTop: 16 },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  count: { fontSize: 11 },
  countPlain: { fontSize: 14, color: T.mutedFg },
  hint: { margin: "4px 0 0", fontSize: 11, color: T.mutedFg },
  grid: { marginTop: 8, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 },
  gridPlain: { marginTop: 10, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 },
});

/*
 * One set of condition photos: four angles, one camera, one upload lifecycle
 * per tile. Every stage of a car's stay is photographed the same way, so the
 * mechanics live here and each screen brings its own words - what the set is
 * called, and what it says in each state. `hints` is keyed by state rather than
 * resolved by the caller so the order the states are read in stays in one place.
 */
export default function PhotoCaptureBlock({
  eyebrow,
  hints,
  photos,
  onCapture,
  onRetry,
  hydrating,
  anyBusy,
  anyError,
  bookingId,
  /* A required set counts down in amber; an optional one stays quiet. */
  required = false,
  /* Boxed beside other panels, or plain where the set is the subject. */
  framed = true,
}) {
  const s = useStyles();
  const ui = useUi();

  /* Which angle's camera is open. Only this view needs to know. */
  const [openAngle, setOpenAngle] = useState(null);
  const open = photoAngles.find((a) => a.angle === openAngle);

  const doneCount = photoAngles.filter(
    ({ angle }) => photos[angle]?.status === "done",
  ).length;
  const allDone = doneCount === photoAngles.length;

  const hint = !bookingId
    ? hints.noBooking
    : hydrating
    ? hints.hydrating
    : anyError
    ? hints.error
    : anyBusy
    ? hints.busy
    : allDone
    ? hints.allDone
    : hints.idle;

  return (
    <div className={framed ? s.box : s.plain}>
      <div className={s.head}>
        <div className={ui.eyebrow}>{eyebrow}</div>
        {framed ? (
          <div
            className={s.count}
            style={{
              color: allDone
                ? T.successText
                : required
                ? T.warning
                : T.mutedFg,
            }}
          >
            {doneCount}/{photoAngles.length}
          </div>
        ) : (
          <div className={s.countPlain}>
            {doneCount}/{photoAngles.length} captured
          </div>
        )}
      </div>
      <p className={s.hint}>{hint}</p>

      <div className={framed ? s.grid : s.gridPlain}>
        {photoAngles.map(({ angle, label }) => {
          const state = photos[angle] ?? {};
          return (
            <PhotoTile
              key={angle}
              label={label}
              imageUrl={state.imageUrl}
              status={state.status}
              progress={state.progress}
              error={state.error}
              onCapture={() => setOpenAngle(angle)}
              onRetry={() => onRetry(angle)}
            />
          );
        })}
      </div>

      {open && (
        <CameraCaptureModal
          label={open.label}
          onCancel={() => setOpenAngle(null)}
          onConfirm={(blob, dims) => {
            setOpenAngle(null);
            onCapture(open.angle, blob, dims);
          }}
        />
      )}
    </div>
  );
}
